import { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import AuthInput from '../components/AuthInput';
import GradientBackground from '../components/GradientBackground';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { isLoggedIn, parentFirebaseLogin, parentRegister } from '../api/auth';
import { authErrorMessage, validateLoginIdentifier, validatePassword } from '../utils/authValidation';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { SocialProvider, signInWithFirebaseProvider } from '../services/firebaseAuth';

type Props = NativeStackScreenProps<RootStackParamList, 'Signup'>;

export default function SignupScreen({ navigation }: Props) {
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoggedIn()) {
      navigation.reset({ index: 0, routes: [{ name: 'Home' }] });
    }
  }, [navigation]);

  const handleSignup = async () => {
    const identifier = loginValue.trim();
    const identifierError = validateLoginIdentifier(identifier);
    const passwordError = validatePassword(password);

    if (identifierError || passwordError) {
      setFormError(identifierError || passwordError || '');
      return;
    }

    setFormError('');
    setLoading(true);
    try {
      await parentRegister(identifier, password);
      navigation.reset({ index: 0, routes: [{ name: 'CreateChildProfile' }] });
    } catch (error) {
      setFormError(authErrorMessage(error, 'Vui lòng kiểm tra thông tin và thử lại.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    setFormError('');
    setLoading(true);
    try {
      const idToken = await signInWithFirebaseProvider(provider);
      const loginData = await parentFirebaseLogin(idToken);
      navigation.reset({
        index: 0,
        routes: [{ name: loginData.is_new_user || (loginData.profiles?.length ?? 0) === 0 ? 'CreateChildProfile' : 'ProfileSelect' }],
      });
    } catch (error) {
      setFormError(authErrorMessage(error, 'Không thể đăng nhập bằng mạng xã hội. Vui lòng thử lại.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.screen}>
      <GradientBackground fullHeight />
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.topRow}>
          <View style={styles.topTextBlock}>
            <Text style={styles.heading}>Tạo tài khoản</Text>
            <Text style={styles.subheading}>Đăng ký tài khoản phụ huynh để tạo hồ sơ nghe riêng cho bé.</Text>
          </View>
          <Pressable
            onPress={() => navigation.reset({ index: 0, routes: [{ name: 'Home' }] })}
            style={styles.homeButton}
            hitSlop={10}
          >
            <Text style={styles.homeButtonText}>Trang chủ</Text>
          </Pressable>
        </View>

        <View style={styles.authBox}>
          <AuthInput
            label="Email hoặc số điện thoại"
            placeholder="name@example.com hoặc 090..."
            value={loginValue}
            onChangeText={(value) => {
              setLoginValue(value);
              if (formError) setFormError('');
            }}
          />
          <AuthInput
            label="Mật khẩu"
            placeholder="Tạo mật khẩu ít nhất 6 ký tự"
            secureTextEntry
            value={password}
            onChangeText={(value) => {
              setPassword(value);
              if (formError) setFormError('');
            }}
          />
          {formError ? <Text style={styles.errorText}>{formError}</Text> : null}
          <Pressable style={styles.mainButton} onPress={handleSignup} disabled={loading}>
            <Text style={styles.mainButtonText}>{loading ? 'Đang tạo tài khoản...' : 'Đăng ký'}</Text>
          </Pressable>
          <SocialLoginButtons disabled={loading} onPress={handleSocialLogin} />

          <View style={styles.footer}>
            <Text style={styles.footerText}>Đã có tài khoản?</Text>
            <Pressable onPress={() => navigation.navigate('Login')} hitSlop={10}>
              <Text style={styles.footerLink}>Đăng nhập</Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 44,
    paddingBottom: 34,
  },
  topRow: {
    minHeight: 78,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  topTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  heading: {
    color: colors.buttonText,
    fontSize: 25,
    fontWeight: '900',
    lineHeight: 31,
  },
  subheading: {
    color: colors.buttonText,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
    marginTop: 6,
  },
  homeButton: {
    minHeight: 36,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.78)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    marginTop: 2,
  },
  homeButtonText: {
    color: colors.textPrimary,
    fontSize: 13,
    fontWeight: '900',
  },
  authBox: {
    borderRadius: 26,
    backgroundColor: 'rgba(255,255,255,0.92)',
    padding: 18,
  },
  errorText: {
    color: colors.danger,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
    marginBottom: 14,
  },
  mainButton: {
    backgroundColor: colors.button,
    borderRadius: 18,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainButtonText: {
    color: colors.buttonText,
    fontWeight: '900',
    fontSize: 14,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 18,
    gap: 4,
  },
  footerText: {
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: '700',
  },
  footerLink: {
    color: colors.textSecondary,
    fontWeight: '900',
    fontSize: 14,
  },
});
